// DS: PO60
import _extends from "@swc/helpers/src/_extends.mjs";
import React, { useEffect } from 'react';
import { compose } from 'redux';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { BiData } from '@wix/da-bi/pkg/events';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import DSModal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import { MAX_TITLE_LENGTH } from '../../../utils/isValidFolderName';
import { useTranslation } from 'react-i18next';
import withCurrentMediaSource from '../../_hocs/withCurrentMediaSource';
import withCurrentGallection from '../../_hocs/withCurrentGallection';
import withGallections from '../../_hocs/withGallections';
import withCurrentGallectionSubfolders from '../../_hocs/withCurrentGallectionSubfolders';
import { connectMediaStore } from '../../_hocs/withOwnReduxStore';
import { createGallection } from '../../actions';
import PremiumForm, { FolderType } from './_partials/PremiumForm';
import { getIsOnlyPaidFoldersSelector } from '../../selectors';
import yupResolver from '../../../utils/yupResolver';
import { dollarsToPoints } from '../../../utils/points';
import { MediaSourceType } from '../../types';
import { getCoreLevel, getCurrentUserHasCore } from '../../../publicSession/selectors';
import useValidation from './useValidation';
import DisclosureBanner from '@wix/da-ds/pkg/banners/DisclosureBanner/DisclosureBanner';
import BuyCoreButton from '../../../Button/BuyCoreButton/BuyCoreButton';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import newGallectionPopupStyles from './NewGallectionPopup.scss';
export const NewGallectionPopup = ({ currentMediaSource , currentFolderId , subfolders , gallections: topLevelGallections , onlyNewPaidFolders =false , coreLevel , hasCoreMembership , onCancel , onDone , createGallection: createGallectionAction  })=>{
    const { t  } = useTranslation();
    const isGallery = currentMediaSource === MediaSourceType.GALLERY;
    const isPrivateCollection = currentMediaSource === MediaSourceType.PRIVATE_COLLECTION;
    const existingFolderNames = (currentFolderId ? subfolders : topLevelGallections).map((gallection)=>gallection.name);
    const validationSchema = useValidation(coreLevel, isGallery, existingFolderNames);
    const formContext = useForm({
        mode: 'onChange',
        resolver: yupResolver(validationSchema, {
            abortEarly: false
        }),
        defaultValues: {
            title: '',
            price: '',
            folderType: onlyNewPaidFolders ? FolderType.PAID : FolderType.REGULAR
        }
    });
    const { register , formState , control , errors , handleSubmit  } = formContext;
    useEffect(()=>{
        // folderType might not be rendered depending on the value of `onlyNewPaidFolders`.
        // we still need the field to be registered for validation purposes
        register('folderType');
    }, [
        register
    ]);
    const withPremiumForm = currentMediaSource === MediaSourceType.GALLERY && !currentFolderId; /* PF can not be a sub-folder */ 
    const onSubmit = (data)=>{
        createGallectionAction({
            name: data.title,
            priceInPoints: dollarsToPoints(data.price),
            folderType: data.folderType
        });
        onDone && onDone();
    };
    const onTitleKeyUp = (ev)=>{
        if (formState.isValid && ev.key === 'Enter') {
            handleSubmit(onSubmit);
        }
    };
    const newFolderKeyPart = currentFolderId ? 'newSubGallery' : 'new';
    const heading = onlyNewPaidFolders ? t(`mediapopup.gallections.premiumFolder.prompt.${newFolderKeyPart}.title`) : t(`mediapopup.gallections.${currentMediaSource}.prompt.${newFolderKeyPart}.title`);
    let subheading = '';
    if (withPremiumForm && onlyNewPaidFolders) {
        subheading = t('mediapopup.newFolderPopup.subtitle.paidFolders');
    }
    if (isPrivateCollection) {
        subheading = t('addToCollectionPopover.private.subtitle');
    }
    return /*#__PURE__*/ React.createElement(DSModal, {
        isOpen: true,
        onRequestClose: onCancel,
        className: newGallectionPopupStyles['root'],
        contentClassName: newGallectionPopupStyles['content'],
        heading: heading,
        subheading: subheading,
        footerRight: [
            /*#__PURE__*/ React.createElement(TextButton, {
                key: "cancel",
                onClick: onCancel,
                size: "large",
                variant: "main"
            }, t('popup.cancel')),
            isPrivateCollection && !hasCoreMembership ? /*#__PURE__*/ React.createElement(BuyCoreButton, {
                size: "large"
            }, t('common.upgradeCoreCTA')) : /*#__PURE__*/ React.createElement(BrandedButton, {
                key: "submit",
                size: "large",
                variant: "brand-green",
                disabled: formState.isSubmitting,
                onClick: handleSubmit(onSubmit)
            }, t('mediapopup.create'))
        ]
    }, /*#__PURE__*/ React.createElement("div", null, isPrivateCollection && !hasCoreMembership && /*#__PURE__*/ React.createElement(DisclosureBanner, {
        className: newGallectionPopupStyles['non-core-banner'],
        message: t('mediapopup.newFolderPopup.gallery.nonCoreBanner.message'),
        ctaLabel: t('common.upgradeCoreCTA'),
        ctaBiData: BiData({
            evid: 375,
            referral_info: ReferralType.PRIVATE_COLLECTIONS_POPUP
        })
    }), /*#__PURE__*/ React.createElement(Controller, {
        name: "title",
        control: control,
        render: ({ onChange , value , name  })=>{
            const error = errors[name];
            return /*#__PURE__*/ React.createElement(TextInput, {
                id: "collection-title-input",
                name: name,
                value: value,
                label: t('mediapopup.gallections.input.name'),
                validationResult: error ? 'error' : undefined,
                details: error === null || error === void 0 ? void 0 : error.message,
                maxLength: MAX_TITLE_LENGTH,
                onChange: onChange,
                onKeyUp: onTitleKeyUp,
                autoFocus: true
            });
        }
    }), withPremiumForm && /*#__PURE__*/ React.createElement(FormProvider, _extends({}, formContext), /*#__PURE__*/ React.createElement(PremiumForm, {
        coreLevel: coreLevel,
        hasCoreMembership: hasCoreMembership,
        onlyNewPaidFolders: onlyNewPaidFolders
    }))));
};
NewGallectionPopup.displayName = 'NewGallectionPopup';
const enhance = compose(withCurrentMediaSource, withCurrentGallection, withGallections, withCurrentGallectionSubfolders, connectMediaStore((state)=>({
        onlyNewPaidFolders: getIsOnlyPaidFoldersSelector(state),
        coreLevel: getCoreLevel(state),
        hasCoreMembership: getCurrentUserHasCore(state)
    }), {
    createGallection
}));
export default enhance(NewGallectionPopup);
